import { forwardRef, useMemo } from 'react'
import { NavLink, matchPath, useLocation } from 'react-router-dom'
import { Tab, TabItem, useTabSlidingUnderline } from '@/common/ui/Tab'

export type CompanyStatsRouteView = 'stats' | 'compare' | 'detailed'

type CompanyStatsViewTabsProps = {
    basePath: string
}

type CompanyStatsTab = {
    view: CompanyStatsRouteView
    label: string
    to: string
}

const buildStatsTabs = (basePath: string): CompanyStatsTab[] => [
    { view: 'stats', label: 'Сводный отчет', to: basePath },
    { view: 'compare', label: 'Сравнение', to: `${basePath}/compare` },
    { view: 'detailed', label: 'Детальное сравнение', to: `${basePath}/detailed` },
]

const CompanyStatsTabLink = forwardRef<HTMLDivElement, CompanyStatsTab>(function CompanyStatsTabLink(
    { label, to },
    ref
) {
    const { search } = useLocation()

    return (
        <TabItem ref={ref}>
            <NavLink
                to={{ pathname: to, search }}
                role='tab'
                aria-label={label}
                tabIndex={0}
                className={({ isActive }) => `tab-link w-full${isActive ? ' active' : ''}`}
                end
            >
                <span className='text-base font-normal whitespace-nowrap'>{label}</span>
            </NavLink>
        </TabItem>
    )
})

export const CompanyStatsViewTabs = ({ basePath }: CompanyStatsViewTabsProps) => {
    const { pathname } = useLocation()

    const tabs = useMemo(() => buildStatsTabs(basePath), [basePath])

    const activeIndex = useMemo(() => {
        const compareIndex = tabs.findIndex(tab => tab.view === 'compare')
        const detailedIndex = tabs.findIndex(tab => tab.view === 'detailed')

        if (matchPath({ path: tabs[compareIndex]?.to ?? '', end: true }, pathname)) return compareIndex
        if (matchPath({ path: tabs[detailedIndex]?.to ?? '', end: true }, pathname)) return detailedIndex
        return 0
    }, [pathname, tabs])

    const { underline, setItemRef } = useTabSlidingUnderline(activeIndex)

    return (
        <Tab
            className='tab2--sliding-indicator'
            underlineIndicator={
                <div
                    className='tab2-sliding-underline'
                    style={{ transform: `translateX(${underline.x}px)`, width: underline.width }}
                    aria-hidden
                />
            }
        >
            {tabs.map((tab, index) => (
                <CompanyStatsTabLink key={tab.view} ref={setItemRef(index)} {...tab} />
            ))}
        </Tab>
    )
}
